import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogMedium } from "@/lib/dialogSize";

interface CacheSettingsValue {
  UseCache: boolean;
  UseDeltas: boolean;
  RebuildCache: boolean;
}

const parseCacheSettings = (value: string | null): CacheSettingsValue | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as CacheSettingsValue;
  } catch {
    return null;
  }
};

const BooleanValue: React.FC<{ value: boolean }> = ({ value }) => (
  <span className={`text-sm ${value ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>
    {value ? "Yes" : "No"}
  </span>
);

const CacheSettingsDisplay: React.FC<{ label: string; settings: CacheSettingsValue | null }> = ({ label, settings }) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm font-bold">{label}:</span>
    {settings ? (
      <div className="flex flex-col gap-0.5 pl-2">
        <div className="flex flex-row gap-1">
          <span className="text-sm font-bold">Use Cache:</span>
          <BooleanValue value={settings.UseCache} />
        </div>
        <div className="flex flex-row gap-1">
          <span className="text-sm font-bold">Use Deltas:</span>
          <BooleanValue value={settings.UseDeltas} />
        </div>
        <div className="flex flex-row gap-1">
          <span className="text-sm font-bold">Rebuild Cache:</span>
          <BooleanValue value={settings.RebuildCache} />
        </div>
      </div>
    ) : (
      <span className="text-sm">N/A</span>
    )}
  </div>
);

const ViewClientCacheSettingsChangeAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
}> = ({ auditLog, open, onClose }) => {
  const oldSettings = parseCacheSettings(auditLog?.oldValue ?? null);
  const newSettings = parseCacheSettings(auditLog?.newValue ?? null);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogMedium} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            {auditLog.additionalData && (
              <>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-bold">
                    Reason:
                  </span>
                  <span
                    className="text-sm whitespace-pre-wrap break-words overflow-y-auto max-h-[15vh] pr-2"
                  >
                    {auditLog.additionalData}
                  </span>
                </div>
                <Separator />
              </>
            )}

            <CacheSettingsDisplay label="Previous Cache Settings" settings={oldSettings} />
            <CacheSettingsDisplay label="New Cache Settings" settings={newSettings} />

            <LabelValuePair
              label="Performed By"
              value={auditLog.performerEmailAddress}
            />

            <LabelValuePair
              label="Event Date"
              value={auditLog.eventDate}
            />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewClientCacheSettingsChangeAuditLogDetailsModal;
